import {
  Button,
  CircularProgress,
  Dialog,
  DialogBody,
  DialogContent,
  FullScreenLoading,
} from '@civilgrid/ui/components';
import Persona from 'persona';
import { useState } from 'react';
import { useSnapshot } from 'valtio';
import { checkIfDomainAllowed, userCompletedVerification, userFailedVerification } from '~/api/user2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import getConfig from '~/config';
import { logOut as apilogOut } from '../../api/auth2';
import bgImage from '../../assets/img/construction-safety.optimized.jpg';
import logoImage from '../../assets/img/logo/lettermarkOrangeLG.png';
import authStore from '../../authStore';
import rootStore from '../../store';
import UserProfileContent from '../common/UserProfile/UserProfileContent';
import ConfirmSkipVerification from './ConfirmSkipVerification';

const config = getConfig();

export default function VerificationComponent() {
  const { me } = useSnapshot(rootStore);
  const [isPersonaLoading, setPersonaLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [isSkipVerificationVisible, setSkipVerificationVisible] = useState(false);

  const personaClient = () => {
    //setVerificationLoading(true);
    const client = new Persona.Client({
      templateId: 'itmpl_wJ7Xk8Pr5wVWnpc4LySUjh3ACSwS',
      environmentId: config.personaEnvironmentId,
      fields: {
        emailAddress: me!.email,
      },
      onReady: () => {
        client.open();
        setShowProfileModal(false);
        setPersonaLoading(false);
      },
      onComplete: async () => {
        try {
          capturePosthogEvent(POSTHOG_EVENTS.verificationCompleted);
          await userCompletedVerification();
          await rootStore.refreshMe();
        } catch (e) {
          console.error(e);
        }
      },
      onError: async (errorCode) => {
        // setVerificationComplete(true);
        console.error(errorCode);
        await userFailedVerification();
        alert('There was an error in the verification process. Please try again or contact support.');
      },
      onCancel: async () => {
        capturePosthogEvent(POSTHOG_EVENTS.verificationCancelled);
        await userFailedVerification();
      },
    });
    return client;
  };

  const onCompletion = () => {
    setPersonaLoading(true);
    personaClient();
  };

  if (me) {
    (async () => {
      const response = await checkIfDomainAllowed();

      if (response.allowedDomain !== undefined) {
        setSkipVerificationVisible(response.allowedDomain);
      }
    })();

    let content = <></>;
    const verificationSent = me.verificationSent;

    if (verificationSent) {
      // waiting...
      content = (
        <>
          <div className="my-4">
            Thank you for completing the identity verification process. Your participation ensures CivilGrid can
            maintain a secure data sharing environment for all users. Verification processing typically takes 15
            minutes, but in some cases can take up to 24 hours. You will receive an e-mail once you’ve been approved.
          </div>

          <Button
            data-testid="verification-logout-button"
            onClick={async () => {
              const email = me.email;
              try {
                const response = await apilogOut({ email });
                authStore.reset();
                if (response.url) {
                  window.location.href = response.url;
                  return;
                }
              } catch {
                authStore.reset();
              }
            }}
          >
            Logout
          </Button>
        </>
      );
    } else {
      content = (
        <div className="space-y-4 text-sm">
          <div>
            CivilGrid partners with utilities, agencies, and data providers in order to bring you their proprietary site
            data in our secure, on-demand platform. In order for you to have access, they require we verify your ID.
          </div>
          <div>
            Your participation helps to maintain a secure data environment for the entire CivilGrid community, and helps
            us all be good stewards of our partners’ proprietary datasets.
          </div>
          <div className="space-y-2">
            <div>
              This verification process should take you about three minutes to complete, and consists of two steps:
            </div>
            <ul className="mx-5 list-outside list-decimal">
              <li>Take a photo of a government-issued photo ID (driver’s license, state ID, passport, etc.).</li>
              <li>Take a selfie.</li>
            </ul>
            <div>
              You can complete this process either with your computer’s webcam or with your phone. Please take a moment
              to retrieve your ID and phone, then click Start Verification below.
            </div>
            <div>
              Once completed, verification processing typically takes 30 minutes, but may take up to one business day.
              You will receive an email once your account has been approved. You will then be able to access CivilGrid
              at{' '}
              <a
                href="https://map.civilgrid.com"
                rel="noreferrer noopener"
                className="text-blue-50 underline hover:text-blue-60"
              >
                https://map.civilgrid.com
              </a>
              .
            </div>
          </div>

          <div className="space-x-2">
            <Button
              variant="secondary"
              data-testid="verification-learn-more-button"
              onClick={async () => {
                capturePosthogEvent(POSTHOG_EVENTS.verificationLearnMoreClicked);
                window.open(
                  'https://civilgrid.notion.site/CivilGrid-Account-Verification-9706938ee1824d048832fe713888a91a',
                  '_blank',
                );
              }}
            >
              Learn More
            </Button>

            {isSkipVerificationVisible && (
              <ConfirmSkipVerification
                personaClient={personaClient}
                isPersonaLoading={isPersonaLoading}
                setPersonaLoading={setPersonaLoading}
              />
            )}

            <Button
              data-testid="verification-start-button"
              onClick={() => {
                setShowProfileModal(true);
              }}
            >
              Start Verification
            </Button>
            <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
              <DialogContent
                title="Start Verification"
                titleProps={{ className: 'text-xl font-normal' }}
                headerProps={{ className: 'px-8 pt-8 pb-0' }}
                preventDismiss
                className="z-[110] h-[650px] max-h-[min(650px,80vh)] w-[400px] max-w-[min(400px,calc(100vw-2rem))] rounded-t-[4px] font-sans text-xs shadow-[0_12px_40px_2px_rgba(0,0,0,0.4)]"
                overlayClassName="z-[110]"
                data-testid="verification-profile-dialog"
              >
                <DialogBody className="px-8 pt-4 pb-8">
                  {isPersonaLoading ? (
                    <div className="flex h-full w-full items-center justify-center">
                      <CircularProgress indeterminate />
                    </div>
                  ) : (
                    <div className="flex h-full flex-col space-y-2">
                      <UserProfileContent placement="verification" onCompletion={onCompletion} />
                    </div>
                  )}
                </DialogBody>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      );
    }
    return (
      <>
        {isPersonaLoading && <FullScreenLoading />}
        <div className="flex h-full w-full">
          <div className="flex h-full w-full flex-col items-center justify-center p-8 md:w-3/4">
            <img src={logoImage} alt="CivilGrid logo" className="my-8 w-60" />
            {content}
          </div>
          <div
            className="hidden bg-cover sm:block"
            style={{
              backgroundImage: `url(${bgImage})`,
              height: '100vh',
              width: '100%',
            }}
          />
        </div>
      </>
    );
  } else {
    return <FullScreenLoading />;
  }
}
